import { Button, Icon } from 'antd'
import React, { useContext, useEffect, useRef, useState } from 'react'
import AuthService from '../classes/AuthService'
import { Camera, Mic, Video, Volume2, Settings, CheckCircle, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react'
import AudioVisualizer from '../pages/protected/streams/components/AudioVisualizerSmall'
import AudioVisualizerSmall from '../pages/protected/streams/components/AudioVisualizerSmall'
import cameraLens from "../assets/images/camera-lens.png";
import allowCamMic from "../assets/images/allow_cam_mic.png";
import GlobalStateContext from '../context'
import { SAVE_ITEM } from '../store/actions/types'
import { useIsMounted,detectCameraType } from '../utils'
import { isEmpty } from 'lodash'

// getLabelFn allows video to match via detectCameraType, audio via device.label
function resolveDeviceId(deviceList, savedName, savedId, getLabelFn = d => d.label) {
    if (savedName) {
        const byName = deviceList.find(d => getLabelFn(d) === savedName)
        if (byName) return byName.deviceId
    }
    if (savedId && deviceList.find(d => d.deviceId === savedId)) return savedId
    return deviceList[0]?.deviceId
}

export default function MediaConfig(props) {
    const [devices, setDevices] = useState({ "audioinput": [], "videoinput": [] })
    const [mediaStream, setMediastream] = useState(null)
    const [mediaDevicesAllowed, setMediaDevicesAllowed] = useState(false)
    const videoPreviewRef = useRef()
    const [mediaSourceKey, setMediaSourceKey] = useState(0)
    const [state, dispatch] = useContext(GlobalStateContext)
    const isMounted = useIsMounted();
    const [selected, setSelected] = useState({ ...state.streamMediaConfig })
    const [isLoading, setisLoading] = useState(true)
    const [isControlsExpanded, setIsControlsExpanded] = useState(false)
    const devicesRef = useRef({ audioinput: [], videoinput: [] })

    const stopTracks = (stream) => {
        console.log("[x]Stopping stream",stream);
        if (stream) {
            stream.getTracks().forEach((track) => {
                track.stop()
                console.log(`Track ${track.id } (${track.label } ) ${track.kind }  stopped`)
            });
        }
    };

    const cleanUp = () => {
        stopTracks(mediaStream);
    }

    const handleRetryPermission = async () => {
        console.log("[x] Retrying permission request");
        setisLoading(true);
        try {
            // Actually request permission instead of reloading page
            await onSelected();
            // If successful, mediaDevicesAllowed will be set to true
        } catch (error) {
            console.error("[x] Permission retry failed:", error);
            setisLoading(false);
        }
    };

    const onSelected = async (properties = null) => {
        console.debug("[x] onSelected")

        try {
            stopTracks(mediaStream)
            properties = properties || {
                audioinput: selected?.audioinput,
                videoinput: selected?.videoinput,
            }
            console.debug("[x] onSelected properties:",properties)

            // Stop loading before getUserMedia so UI shows permission instructions
            // while browser permission popup is displayed
            setisLoading(false);

            const audioId = properties.audioinput || undefined;
            const videoId = properties.videoinput || undefined;
            let _mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: audioId ? { deviceId: { exact: audioId } } : true,
                video: videoId ? { deviceId: { exact: videoId } } : true,
            })

            console.log("cat", properties, devices);

            setMediastream(_mediaStream)
            setMediaDevicesAllowed(true)

            // Re-enumerate AFTER getUserMedia so device labels are populated (especially Firefox)
            await getDevices();
            const d = devicesRef.current
            const audioDevice = d.audioinput.find(x => x.deviceId === properties.audioinput)
            const videoDevice = d.videoinput.find(x => x.deviceId === properties.videoinput)
            const newConfig = {
                ...state.streamMediaConfig,
                audioinput: properties.audioinput,
                audioinputName: audioDevice?.label || '',
                videoinput: properties.videoinput,
                videoinputName: videoDevice ? detectCameraType(videoDevice) : '',
                permission: true,
            }
            dispatch({ type: SAVE_ITEM, payload: ['streamMediaConfig', newConfig] })
            new AuthService().updateProfile({ media_config: newConfig }).catch(() => {})

        } catch (error) {
            console.log("cate", error)
            if (["NotAllowedError", "NotFoundError", "DEVICE_ACCESS_DENIED"].includes(error.name)) {
                setMediaDevicesAllowed(false)
                dispatch({
                    type: SAVE_ITEM,
                    payload: ['streamMediaConfig', { ...state.streamMediaConfig, ...properties, permission: false }]
                })
            }
            setisLoading(false);
        }
    }

    const onDeviceChange = (event, type) => {
        console.log("[x] OndeviceChange ",type)
        const value = event.target.value;
        console.log("[x] OndeviceChange Value ",value)

        const newSelected = { ...selected, [type]: value }
        setSelected(newSelected)
        onSelected(newSelected)
    }

    const getDevices = async () => {
        console.log("Getting devices...")
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            const _devices = { "audioinput": [], "videoinput": [] };
            for (let device of all) {
                if (_devices[device.kind]) _devices[device.kind].push(device);
            }
            console.log("[x] Setting device list", _devices);
            devicesRef.current = _devices;
            setDevices(_devices);
            return _devices;
        } catch (err) {
            console.log("[x] Failed to get devices", err);
            setMediaDevicesAllowed(false);
            throw err;
        }
    }

    useEffect(() => {
        if (videoPreviewRef.current) {
            videoPreviewRef.current.srcObject = mediaStream
        }

        return () => {
            cleanUp()
        }
    }, [mediaStream])

    useEffect(() => {
        // Only react when a previously-selected device disappears (plug/unplug).
        // Initial selection is handled in initialize(); don't call onSelected here on first load.
        if (devices.audioinput.length === 0 && devices.videoinput.length === 0) return;
        if (!selected?.audioinput && !selected?.videoinput) return; // not yet initialized

        const audioGone = selected?.audioinput && !devices.audioinput.find(d => d.deviceId === selected.audioinput);
        const videoGone = selected?.videoinput && !devices.videoinput.find(d => d.deviceId === selected.videoinput);

        if (audioGone || videoGone) {
            const fallback = {
                audioinput: audioGone ? devices.audioinput[0]?.deviceId : selected.audioinput,
                videoinput: videoGone ? devices.videoinput[0]?.deviceId : selected.videoinput,
            };
            setSelected(fallback);
            onSelected(fallback);
        }
    }, [devices])

    useEffect(() => {
        // When permission is granted the video element mounts for the first time.
        // By then mediaStream may already be set, so apply it here.
        if (mediaDevicesAllowed && videoPreviewRef.current && mediaStream) {
            videoPreviewRef.current.srcObject = mediaStream
        }
    }, [mediaDevicesAllowed])

    // Listen for device changes (plug/unplug)
    useEffect(() => {
        if (!mediaDevicesAllowed) return;

        const handleDeviceChange = async () => {
            console.log("[x] Device change detected (plug/unplug)");
            try {
                await getDevices();
            } catch (error) {
                console.error("[x] Failed to re-enumerate devices:", error);
            }
        };

        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
        };
    }, [mediaDevicesAllowed])

    // Initialize by requesting permission first
    useEffect(() => {
        const initialize = async () => {
            setisLoading(true);
            try {
                const savedConfig = state.streamMediaConfig;
                const deviceList = await getDevices();
                // Resolve saved device name → current deviceId (IDs change across sessions)
                const resolved = {
                    audioinput: resolveDeviceId(deviceList.audioinput, savedConfig?.audioinputName, savedConfig?.audioinput),
                    videoinput: resolveDeviceId(deviceList.videoinput, savedConfig?.videoinputName, savedConfig?.videoinput, detectCameraType),
                };
                setSelected(resolved);
                await onSelected(resolved);
            } catch (error) {
                console.error("[x] Initialization failed:", error);
                setMediaDevicesAllowed(false);
                setisLoading(false);
            }
        };

        initialize();
    }, [])

    return (
        <>
            {isLoading ? (
                <div className='flex items-center justify-center py-4'>
                    <div className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                        <span className='text-gray-600 font-medium text-sm'>Setting up studio...</span>
                    </div>
                </div>
            ) : (
                <>
                    {mediaDevicesAllowed == false ? (
                        <div className='bg-white rounded-xl border border-gray-200 p-4 text-center max-w-2xl mx-auto'>
                            {/* Header */}
                            <div className="mb-4">
                                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <AlertCircle className="w-6 h-6 text-red-600" />
                                </div>
                                <h3 className='text-xl font-bold text-gray-900 mb-2'>Camera & Microphone Access Required</h3>
                                <p className="text-gray-600 text-sm">Please allow access to your camera and microphone.</p>
                            </div>

                            {/* Instructions Image */}
                            <div className='mb-4'>
                                <div className="bg-gray-50 rounded-lg p-3 inline-block">
                                    <img src={allowCamMic} className='max-w-full h-auto rounded-lg shadow-sm' alt="Allow camera and microphone access" />
                                </div>
                            </div>

                            {/* Instructions */}
                            <div className='text-left bg-gray-50 rounded-lg p-4 mb-4'>
                                <h4 className='text-base font-semibold text-gray-900 mb-3 flex items-center'>
                                    <Settings className="w-4 h-4 mr-2 text-blue-600" />
                                    How to Enable Access
                                </h4>

                                <ol className='list-decimal space-y-2 pl-5 text-gray-700 text-sm'>
                                    <li>Click "Allow" when prompted for camera/microphone access.</li>
                                    <li>If no prompt appears, check your browser's address bar for a camera icon.</li>
                                    <li>Click the icon and select "Allow" for this site.</li>
                                </ol>
                            </div>

                            {/* Action Button */}
                            <button
                                className='bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                onClick={handleRetryPermission}
                            >
                                I have allowed access
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white sm:rounded-xl  overflow-hidden">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
                                <div className="flex items-center space-x-2">
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-900">Get Ready</h3>
                                        <p className="text-xs text-gray-600">Configure your camera and microphone</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
                                {/* Video Preview */}
                                <div className="lg:col-span-2">
                                    <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg">
                                        <video 
                                            key={`video_${mediaSourceKey}`} 
                                            poster={cameraLens} 
                                            preload='auto' 
                                            ref={videoPreviewRef} 
                                            autoPlay 
                                            className='w-full h-48 lg:h-56 object-cover video-mirror'
                                            muted
                                        />
                                        
                                        {/* Status Indicator */}
                                        <div className="absolute top-2 right-2">
                                            <div className="flex items-center space-x-1 bg-black bg-opacity-50 rounded-full px-2 py-1">
                                                <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                                                <span className="text-white text-xs font-medium">Live</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Controls */}
                                <div className="space-y-4">
                                    {/* Mobile Collapsible Controls */}
                                    <div className="lg:hidden">
                                        <button
                                            onClick={() => setIsControlsExpanded(!isControlsExpanded)}
                                            className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <Settings className="w-4 h-4 text-blue-600" />
                                                <span className="text-sm font-medium text-gray-700">Device Settings</span>
                                            </div>
                                            {isControlsExpanded ? (
                                                <ChevronUp className="w-4 h-4 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4 text-gray-500" />
                                            )}
                                        </button>
                                    </div>

                                    {/* Controls Content */}
                                    <div className={`space-y-4 ${!isControlsExpanded ? 'hidden lg:block' : ''}`}>
                                        {/* Video Input */}
                                        <div className="space-y-1">
                                            <label className="flex items-center text-sm font-medium text-gray-700">
                                                <Camera className="w-3 h-3 mr-1 text-blue-600" />
                                                Video Input
                                            </label>
                                            <select 
                                                id="videoInputDevice" 
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 text-sm"
                                                onChange={_ => onDeviceChange(_, "videoinput")}
                                                defaultValue={selected.videoinput}
                                            >
                                                {devices.videoinput?.map(device =>
                                                    <option key={`video_input_option_${device.deviceId}`} value={device.deviceId}>
                                                        {detectCameraType(device)}
                                                    </option>
                                                )}
                                            </select>
                                        </div>

                                        {/* Audio Input */}
                                        <div className="space-y-1">
                                            <label className="flex items-center text-sm font-medium text-gray-700">
                                                <Mic className="w-3 h-3 mr-1 text-blue-600" />
                                                Audio Input
                                            </label>
                                            <select 
                                                id="audioInputDevice" 
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 text-sm"
                                                defaultValue={selected?.audioinput}
                                                onChange={_ => onDeviceChange(_, "audioinput")}
                                            >
                                                {devices.audioinput.map(device =>
                                                    <option key={`audio_input_option_${device.deviceId}`} value={device.deviceId}>
                                                        {device.label}
                                                    </option>
                                                )}
                                            </select>
                                        </div>

                                        {/* Audio Visualizer */}
                                        {mediaStream && (
                                            <div className="space-y-1">
                                                <label className="flex items-center text-sm font-medium text-gray-700">
                                                    <Volume2 className="w-3 h-3 mr-1 text-blue-600" />
                                                    Audio Level
                                                </label>
                                                <div className="bg-gray-50 rounded-md p-2">
                                                    <AudioVisualizerSmall mediaStream={mediaStream} />
                                                </div>
                                            </div>
                                        )}

                                        {/* Status */}
                                        <div className="bg-green-50 rounded-md p-2">
                                            <div className="flex items-center space-x-2">
                                                <CheckCircle className="w-4 h-4 text-green-600" />
                                                <span className="text-sm font-medium text-green-800">Studio Ready</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    )
}
